import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ClipboardText } from "@phosphor-icons/react";
import { Header, MobileNav } from "@/components/Header";
import StatusBadge from "@/components/StatusBadge";
import api from "@/lib/api";
import { useBuilding } from "@/context/BuildingContext";
import { categoryByKey, formatDateShort, formatTime } from "@/lib/constants";

export default function ReportsList() {
  const BUILDING = useBuilding();
  const [reports, setReports] = useState(null);

  useEffect(() => {
    api.get("/reports").then((r) => setReports(r.data)).catch(() => setReports([]));
  }, []);

  return (
    <div className="min-h-screen pb-24 md:pb-0" data-testid="reports-list-page">
      <Header />
      <main className="mx-auto max-w-3xl px-5 py-10 sm:py-14">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{BUILDING.name}</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-[-0.02em] sm:text-5xl">Signalements</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Toutes les demandes de la résidence et leur état d'avancement, en transparence.
          </p>
        </motion.div>

        <div className="mt-8 space-y-4">
          {reports === null && (
            <div className="space-y-4" data-testid="reports-loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-28 animate-pulse rounded-3xl bg-black/5" />
              ))}
            </div>
          )}
          {reports !== null && reports.length === 0 && (
            <div className="rounded-3xl border border-black/5 bg-white p-10 text-center" data-testid="reports-empty">
              <ClipboardText size={40} weight="duotone" className="mx-auto text-muted-foreground" />
              <p className="mt-4 font-display text-lg font-bold">Aucun signalement pour le moment</p>
              <p className="mt-1 text-sm text-muted-foreground">Tout va bien dans la résidence.</p>
              <Link
                to="/signaler"
                data-testid="empty-create-report-link"
                className="mt-6 inline-block rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground hover:bg-[#265442]"
              >
                Signaler un problème
              </Link>
            </div>
          )}
          {reports !== null && reports.map((r, i) => {
            const { Icon, label } = categoryByKey(r.category);
            return (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: Math.min(i * 0.06, 0.5) }}
              >
                <Link
                  to={`/suivi/${r.ref}`}
                  data-testid={`report-card-${r.ref}`}
                  className="group flex items-center gap-4 rounded-3xl border border-black/5 bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-shadow duration-300 hover:shadow-[0_14px_36px_rgb(0,0,0,0.08)]"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <Icon size={24} weight="duotone" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="font-display text-base font-bold tracking-tight">{label}</span>
                      <span className="ml-auto"><StatusBadge status={r.status} /></span>
                    </span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {formatDateShort(r.created_at)} à {formatTime(r.created_at)} · <span className="font-mono font-bold">{r.ref}</span>
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-muted-foreground" data-testid={`report-card-place-${r.ref}`}>
                      {r.sub_building || r.location}
                    </span>
                    {r.location_detail ? (
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">{r.location_detail}</span>
                    ) : null}
                    <span className="mt-1.5 line-clamp-1 block text-sm text-foreground/70">{r.description}</span>
                  </span>
                  <ArrowRight size={18} weight="bold" className="shrink-0 text-muted-foreground transition-transform duration-300 group-hover:translate-x-1 group-hover:text-primary" />
                </Link>
              </motion.div>
            );
          })}
        </div>
      </main>
      <MobileNav />
    </div>
  );
}
